import { useState, useContext, useRef, useEffect } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import ThDailyView   from './ThDailyView';
import ThWeeklyView  from './ThWeeklyView';
import ThMonthlyView from './ThMonthlyView';
import ChatWidget    from '../../components/ChatWidget';

// ── Constants ─────────────────────────────────────────────────────────────────

const TABS = [
  {
    id:    'daily',
    label: 'Daily',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
        <circle cx="12" cy="16" r="1.5" fill="currentColor" stroke="none"/>
      </svg>
    ),
  },
  {
    id:    'weekly',
    label: 'Weekly',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
        <line x1="7" y1="15" x2="17" y2="15" strokeWidth="2.5"/>
      </svg>
    ),
    subItems: [
      {
        id: 'charts',
        label: 'Charts',
      },
      {
        id: 'geography',
        label: 'Geography',
      },
    ],
  },
  {
    id:    'monthly',
    label: 'Monthly',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
        <polyline points="16 7 22 7 22 13"/>
      </svg>
    ),
  },
];

// Internal value → display label (must match CatIcon keys)
const TH_CATS = [
  { value: 'Food',        label: 'Food Delivery' },
  { value: 'RideHailing', label: 'Ride Hailing'  },
];

const VERTICALS   = { Food: 'food', RideHailing: 'ridehailing' };
const HAS_MONTHLY = new Set(['ridehailing']);

// ── Category icon — mirrors Sidebar.js CatIcon ────────────────────────────────
function CatIcon({ label, size = 18 }) {
  const s = { width: size, height: size };
  if (label === 'Ride Hailing') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 17H3a2 2 0 01-2-2V9l3-5h11l3 5v6a2 2 0 01-2 2h-2"/>
      <circle cx="7.5" cy="17.5" r="2.5"/><circle cx="16.5" cy="17.5" r="2.5"/>
    </svg>
  );
  if (label === 'Food Delivery') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/>
      <line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/>
    </svg>
  );
  return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}

// ── Coming-soon placeholder ───────────────────────────────────────────────────

function ComingSoon({ label }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: 360, gap: 12,
      color: 'var(--text-muted)',
    }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.4">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)' }}>
        {label} view coming soon
      </div>
    </div>
  );
}

// ── Left sidebar ──────────────────────────────────────────────────────────────

function ThSidebar({ activeTab, weeklySubView, onTabChange, onWeeklySubChange, category, onCategoryChange, onGoBack }) {
  const { user, logout, isEmailUser } = useAuth();
  const [catOpen,      setCatOpen]      = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const catRef  = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (catRef.current  && !catRef.current.contains(e.target))  setCatOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const catEntry = TH_CATS.find(c => c.value === category) || TH_CATS[0];

  return (
    <aside className="sidebar sidebar-new">

      {/* ── Category selector — identical markup to UK Sidebar ── */}
      <div className="sb-cat-wrap" ref={catRef}>
        <button
          className={`sb-cat-btn ${catOpen ? 'open' : ''}`}
          onClick={() => setCatOpen(o => !o)}
        >
          <div className="sb-cat-icon">
            <CatIcon label={catEntry.label} />
          </div>
          <div className="sb-cat-text">
            <div className="sb-cat-name">{catEntry.label}</div>
            <div className="sb-cat-sub">Pro</div>
          </div>
          <svg className={`sb-cat-arrow ${catOpen ? 'up' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        {catOpen && (
          <div className="sb-cat-dropdown">
            <div className="sb-cat-dropdown-label">Select category</div>
            {TH_CATS.map(({ value, label }) => (
              <button
                key={value}
                className={`sb-cat-group-btn ${category === value ? 'active' : ''}`}
                onClick={() => { onCategoryChange(value); setCatOpen(false); }}
              >
                <span className="sb-cat-group-icon"><CatIcon label={label} size={15} /></span>
                <span>{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── View navigation — uses same sb-nav / sb-section CSS as UK ── */}
      <div className="sb-nav">
        <div className="sb-section">
          <button className="sb-section-btn open" disabled style={{ cursor: 'default' }}>
            <span className="sb-section-btn-label">View</span>
          </button>
          <div className="sb-section-body">
            {TABS.map(tab => (
              <div key={tab.id}>
                <button
                  className={`sb-item${activeTab === tab.id ? ' active' : ''}`}
                  onClick={() => onTabChange(tab.id)}
                >
                  <span className="sb-item-icon">{tab.icon}</span>
                  <span className="sb-item-label">{tab.label}</span>
                </button>
                {tab.subItems && activeTab === tab.id && (
                  <div className="sb-children">
                    {tab.subItems.map(sub => (
                      <button
                        key={sub.id}
                        className={`sb-child${weeklySubView === sub.id ? ' active' : ''}`}
                        onClick={() => onWeeklySubChange(sub.id)}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── User card + dropdown — same as UK sidebar ── */}
      <div className="sidebar-footer" ref={userRef}>
        {userMenuOpen && (
          <div className="sb-user-menu">
            <div className="sb-user-menu-header">
              <div className="sb-user-menu-name">{user?.displayName || 'User'}</div>
              <div className="sb-user-menu-email">{user?.email}</div>
            </div>

            <button
              className="sb-user-menu-item"
              onClick={() => { setUserMenuOpen(false); onGoBack(); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              Change market
            </button>

            {isEmailUser && (
              <button className="sb-user-menu-item" onClick={() => setUserMenuOpen(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                Change password
              </button>
            )}

            <button
              className="sb-user-menu-item sb-user-menu-signout"
              onClick={() => { setUserMenuOpen(false); logout(); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign out
            </button>
          </div>
        )}

        <div
          className={`user-card${userMenuOpen ? ' active' : ''}`}
          onClick={() => setUserMenuOpen(o => !o)}
          style={{ cursor: 'pointer' }}
        >
          {user?.photoURL
            ? <img src={user.photoURL} alt="avatar" className="user-avatar" />
            : <div className="user-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                {user?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
              </div>
          }
          <div style={{ overflow: 'hidden', flex: 1 }}>
            <div className="user-name">{user?.displayName || 'User'}</div>
            <div className="user-email">{user?.email}</div>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
            style={{ flexShrink: 0, opacity: 0.4, transform: userMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>
    </aside>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

export default function ThailandDashboard({ onBack }) {
  const { dark, toggle } = useContext(ThemeContext);
  const [activeTab,      setActiveTab]      = useState('daily');
  const [weeklySubView,  setWeeklySubView]  = useState('charts');
  const [category,       setCategory]       = useState('Food');

  const vertical = VERTICALS[category] || 'food';

  const catLabel = TH_CATS.find(c => c.value === category)?.label || category;
  const subLabel = activeTab === 'weekly'
    ? (weeklySubView === 'geography' ? ' · Geography' : ' · Charts')
    : '';
  const tabLabel = TABS.find(t => t.id === activeTab)?.label || '';
  const title    = `${catLabel} · ${tabLabel}${subLabel}`;

  function handleTabChange(id) {
    setActiveTab(id);
    if (id === 'weekly') setWeeklySubView('charts');
  }

  function renderContent() {
    switch (activeTab) {
      case 'daily':
        return <ThDailyView key={vertical} vertical={vertical} />;
      case 'weekly':
        return <ThWeeklyView key={`${vertical}-${weeklySubView}`} vertical={vertical} subView={weeklySubView} />;
      case 'monthly':
        return HAS_MONTHLY.has(vertical)
          ? <ThMonthlyView key={vertical} vertical={vertical} />
          : <ComingSoon label={`${catLabel} Monthly`} />;
      default:
        return null;
    }
  }

  return (
    <div className="app-shell">
      <ThSidebar
        activeTab={activeTab}
        weeklySubView={weeklySubView}
        onTabChange={handleTabChange}
        onWeeklySubChange={setWeeklySubView}
        category={category}
        onCategoryChange={setCategory}
        onGoBack={onBack}
      />

      <div className="main-content">
        {/* Topbar */}
        <div className="topbar">
          <div className="topbar-title">{title}</div>
          <div className="topbar-spacer" />
          <button className="btn-icon" title={dark ? 'Light mode' : 'Dark mode'} onClick={toggle}>
            {dark ? '☀️' : '🌙'}
          </button>
          <button className="btn-home" title="Back to country select" onClick={onBack}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Back
          </button>
        </div>

        {/* Content */}
        <div className="page-body">
          {renderContent()}
        </div>
      </div>

      <ChatWidget />
    </div>
  );
}
